import {
  BadgeContainer,
  BadgeImage,
  ContentContainer,
} from 'containers/Mobile/Profile/HorizentalFolderView/styles';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IProviderItemView } from 'store/providers/providerTypes';
import styled from 'styled-components';
import LocalIcon from 'ui/LocalIcon';
import {
  IconColorFinder,
  IconLogoFinder,
} from 'utils/check-file-type/findIconViaType';
import { black, boxShadow1 } from 'utils/style/colors';
import { summerizeText } from 'utils/summerizeText';

const WideMobileViewer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 4rem 1fr;
  grid-gap: 1rem;
`;

const DisplayModeContainer = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4rem;
`;

const XLGVerticalItem = styled.div`
  height: 32rem;
  width: 330px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  box-shadow: ${boxShadow1};
  border-radius: 1rem;
`;

const XLGMobileVerticalItem = styled(XLGVerticalItem)`
  width: 310px;
`;

const XLGVerticalImage = styled.img`
  width: 100%;
  height: 80%;
  object-fit: cover;
  border-radius: 0.5rem;
`;

const XLGNormalImage = styled.img`
  width: 50%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
  object-fit: cover;
  object-position: start;
`;

const XLGVerticalContentContainer = styled.div`
  width: 100%;
  height: 20%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`;

const XLGNormalContentContainer = styled.div`
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 1rem;
`;

const XLGNormalItem = styled.div`
  height: 16rem;
  width: 430px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${boxShadow1};
  padding: 1rem;
  border-radius: 1rem;
`;

const XLGMobileNormalItem = styled(XLGNormalItem)`
  width: 312px;
`;

const LGHorizontalItem = styled(XLGVerticalItem)`
  height: 11rem;
  width: 440px;
`;
const LGNormalItem = styled(XLGVerticalItem)`
  height: 11rem;
  width: 125px;
`;
const LGMobileHorizontalItem = styled(LGHorizontalItem)`
  width: 315px;
`;

const LGMobileNormalItem = styled(LGNormalItem)`
  width: 150px;
`;

const LGHorizontalImage = styled(XLGVerticalImage)``;
const LGHorizontalContentContainer = styled(XLGVerticalContentContainer)``;

const LGNormalImage = styled(XLGVerticalImage)``;
const LGNormalContentContainer = styled(XLGVerticalContentContainer)``;

const MDItem = styled(XLGVerticalItem)`
  width: 306px;
  height: 193px;
`;

const MDMoblieItem = styled(XLGVerticalItem)`
  width: 312px;
  height: 193px;
`;

const MDItemImage = styled(XLGVerticalImage)``;
const MDItemContentContainer = styled(XLGVerticalContentContainer)``;

const ShineItem = styled(XLGVerticalItem)`
  width: 215px;
  height: 185px;
`;
const ShineMobileItem = styled(XLGVerticalItem)`
  width: 150px;
  height: 185px;
`;

const ShineItemImage = styled(XLGVerticalImage)``;
const ShineItemContentContainer = styled(XLGVerticalContentContainer)``;

const SmallCellSelector = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${boxShadow1};
  border-radius: 1rem;
`;

export type TDisplayMode =
  | 'display_xlg'
  | 'display_lg'
  | 'display_md'
  | 'display_sm'
  | 'display_shine';

interface IProps {
  item: IProviderItemView;
  displayMode: TDisplayMode;
  cellType: 'lg' | 'md';
  thumbnailUrl: string;
}

const ItemPreviewer: FC<IProps> = ({
  item,
  displayMode,
  cellType,
  thumbnailUrl,
}) => {
  const { t } = useTranslation('modals');

  const getName = useCallback((item: IProviderItemView) => {
    if (item.name) return item.name;
    if (item.type === 'link') return (item as any).providerName;
  }, []);

  const getSmallComponent = () => {
    return (
      <SmallCellSelector>
        <BadgeContainer color={IconColorFinder(item.type)}>
          {thumbnailUrl ? (
            <BadgeImage src={`${thumbnailUrl}`} />
          ) : (
            <LocalIcon
              iconName={IconLogoFinder({
                type: item.type,
                format: item.document?.properties?.format,
              })}
              color={black}
              type="sm"
            />
          )}
        </BadgeContainer>
        <ContentContainer>
          {summerizeText(getName(item) ?? '', 25)}
        </ContentContainer>
      </SmallCellSelector>
    );
  };

  const getComponentBaseOnItsType = () => {
    if (displayMode === 'display_xlg') {
      return (
        <>
          {cellType === 'lg' ? (
            <>
              <XLGVerticalItem>
                <XLGVerticalImage src={thumbnailUrl} />
                <XLGVerticalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </XLGVerticalContentContainer>
              </XLGVerticalItem>
              <XLGMobileVerticalItem>
                <XLGVerticalImage src={thumbnailUrl} />
                <XLGVerticalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </XLGVerticalContentContainer>
              </XLGMobileVerticalItem>
            </>
          ) : (
            <>
              <XLGNormalItem>
                <XLGNormalImage src={thumbnailUrl} />
                <XLGNormalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </XLGNormalContentContainer>
              </XLGNormalItem>
              <XLGMobileNormalItem>
                <XLGNormalImage src={thumbnailUrl} />
                <XLGNormalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </XLGNormalContentContainer>
              </XLGMobileNormalItem>
            </>
          )}
        </>
      );
    } else if (displayMode === 'display_lg') {
      return (
        <>
          {cellType === 'lg' ? (
            <>
              <LGHorizontalItem>
                <LGHorizontalImage src={thumbnailUrl} />
                <LGHorizontalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </LGHorizontalContentContainer>
              </LGHorizontalItem>
              <LGMobileHorizontalItem>
                <LGHorizontalImage src={thumbnailUrl} />
                <LGHorizontalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </LGHorizontalContentContainer>
              </LGMobileHorizontalItem>
            </>
          ) : (
            <>
              <LGNormalItem>
                <LGNormalImage src={thumbnailUrl} />
                <LGNormalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </LGNormalContentContainer>
              </LGNormalItem>
              <LGMobileNormalItem>
                <LGNormalImage src={thumbnailUrl} />
                <LGNormalContentContainer>
                  {summerizeText(getName(item) ?? '', 25)}
                </LGNormalContentContainer>
              </LGMobileNormalItem>
            </>
          )}
        </>
      );
    } else if (displayMode === 'display_md') {
      return (
        <>
          <MDItem>
            <MDItemImage src={thumbnailUrl} />
            <MDItemContentContainer>
              {summerizeText(getName(item) ?? '', 25)}
            </MDItemContentContainer>
          </MDItem>
          <MDMoblieItem>
            <MDItemImage src={thumbnailUrl} />
            <MDItemContentContainer>
              {summerizeText(getName(item) ?? '', 25)}
            </MDItemContentContainer>
          </MDMoblieItem>
        </>
      );
    } else if (displayMode === 'display_shine') {
      return (
        <>
          <ShineItem>
            <ShineItemImage src={thumbnailUrl} />
            <ShineItemContentContainer>
              {summerizeText(getName(item) ?? '', 25)}
            </ShineItemContentContainer>
          </ShineItem>
          <ShineMobileItem>
            <ShineItemImage src={thumbnailUrl} />
            <ShineItemContentContainer>
              {summerizeText(getName(item) ?? '', 25)}
            </ShineItemContentContainer>
          </ShineMobileItem>
        </>
      );
    }
    return (
      <>
        {getSmallComponent()}
        {getSmallComponent()}
      </>
    );
  };

  return (
    <WideMobileViewer>
      <DisplayModeContainer>{t('wideScreenMode')}</DisplayModeContainer>
      <DisplayModeContainer>{t('mobileScreenMode')}</DisplayModeContainer>
      {getComponentBaseOnItsType()}
    </WideMobileViewer>
  );
};

export default ItemPreviewer;
